import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Users, Search, ShieldCheck, Building2, KeyRound, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const ROLE_STYLE = {
  admin: "bg-[#2E3A46]/10 text-[#2E3A46] border-[#2E3A46]/30",
  authority: "bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30",
};
const STATUS_STYLE = {
  active: "bg-[#4A7C59]/10 text-[#4A7C59] border-[#4A7C59]/30",
  pending: "bg-[#C99B4B]/15 text-[#8A6A2E] border-[#C99B4B]/40",
  rejected: "bg-[#9E4747]/10 text-[#9E4747] border-[#9E4747]/30",
  suspended: "bg-[#9E4747]/10 text-[#9E4747] border-[#9E4747]/30",
};

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [resetUser, setResetUser] = useState(null);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch(() => toast.error("Could not load users"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const toggleAccess = async (u) => {
    const suspend = u.status !== "suspended";
    try {
      await api.put(`/admin/users/${u.id}/access`, { suspend });
      toast.success(suspend ? `Access withdrawn for ${u.email}` : `Access restored for ${u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update access");
    }
  };

  const openReset = (u) => { setResetUser(u); setPwd(""); setPwd2(""); };

  const submitReset = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pwd !== pwd2) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/users/${resetUser.id}/password`, { password: pwd });
      toast.success(`Password updated for ${resetUser.email}`);
      setResetUser(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update password");
    }
    setSaving(false);
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) =>
      [u.name, u.email, u.organization, u.role].filter(Boolean).some((v) => v.toLowerCase().includes(s))
    );
  }, [users, q]);

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-users-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">Admin · directory</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#2E3A46] text-white"><Users size={18} /></span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]">Staff accounts</h1>
      </div>
      <p className="mt-2 max-w-2xl font-body text-sm text-[#5C6873]">
        Authority and administrator accounts and the email addresses in use. Family accounts are excluded to
        keep sensitive personal data private.
      </p>

      <div className="mt-6 max-w-md">
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7F82]" />
          <Input
            placeholder="Search by name, email or organisation…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 border-[#E2E8F0] bg-white pl-9"
            data-testid="users-search-input"
          />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between font-body text-sm text-[#5C6873]">
        <span data-testid="users-count">{filtered.length} account{filtered.length === 1 ? "" : "s"}</span>
      </div>

      <div className="mt-3 space-y-2" data-testid="users-list">
        {loading && <p className="font-body text-sm text-[#5C6873]" data-testid="users-loading">Loading…</p>}
        {!loading && filtered.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8F9FA] p-10 text-center font-body text-sm text-[#5C6873]" data-testid="users-empty">
            No matching accounts.
          </div>
        )}
        {filtered.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`user-row-${u.id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-heading text-base font-semibold text-[#1A2024]" data-testid={`user-name-${u.id}`}>{u.name}</h3>
                <Badge variant="outline" className={`rounded-full text-[11px] capitalize ${ROLE_STYLE[u.role] || ""}`} data-testid={`user-role-${u.id}`}>
                  {u.role === "admin" && <ShieldCheck size={11} className="mr-1" />}{u.role}
                </Badge>
                {((u.role === "authority" && u.status) || u.status === "suspended") && (
                  <Badge variant="outline" className={`rounded-full text-[11px] capitalize ${STATUS_STYLE[u.status] || ""}`} data-testid={`user-status-${u.id}`}>
                    {u.status}
                  </Badge>
                )}
                {u.id === me?.id && (
                  <Badge variant="outline" className="rounded-full border-[#E2E8F0] text-[11px] text-[#6B7F82]">you</Badge>
                )}
              </div>
              <div className="mt-0.5 font-body text-sm text-[#5C6873]" data-testid={`user-email-${u.id}`}>{u.email}</div>
              <div className="font-body text-xs text-[#5C6873]">
                {u.organization ? <span className="inline-flex items-center gap-1"><Building2 size={12} /> {u.organization}</span> : "No organisation"}
                {u.created_at && <span> · joined {new Date(u.created_at).toLocaleDateString()}</span>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="border-[#E2E8F0] text-[#2E3A46]"
                onClick={() => openReset(u)}
                data-testid={`user-reset-${u.id}`}
              >
                <KeyRound size={14} className="mr-1" /> Reset password
              </Button>
              {u.id !== me?.id && (
                u.status === "suspended" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#4A7C59]/40 text-[#4A7C59] hover:bg-[#4A7C59]/10"
                    onClick={() => toggleAccess(u)}
                    data-testid={`user-restore-${u.id}`}
                  >
                    <RotateCcw size={14} className="mr-1" /> Restore access
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#9E4747]/40 text-[#9E4747] hover:bg-[#9E4747]/10"
                    onClick={() => toggleAccess(u)}
                    data-testid={`user-withdraw-${u.id}`}
                  >
                    <Ban size={14} className="mr-1" /> Withdraw access
                  </Button>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new password for <span className="font-medium text-[#1A2024]">{resetUser?.email}</span>. They will be able to sign in with it immediately.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReset} className="space-y-4">
            <div>
              <Label htmlFor="new-pwd">New password</Label>
              <Input id="new-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} minLength={6} required className="mt-1.5 h-11" data-testid="reset-new-password-input" />
            </div>
            <div>
              <Label htmlFor="new-pwd2">Confirm password</Label>
              <Input id="new-pwd2" type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required className="mt-1.5 h-11" data-testid="reset-confirm-password-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setResetUser(null)} data-testid="reset-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="reset-save-btn">
                {saving ? "Saving…" : "Update password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
